import React, { useCallback, useEffect, useState } from 'react';
import { ServerInterface } from "src/store/mine_craft/serversSlice";
import { fetchServerSessions } from "src/actions/mine_craft/serversActions";
import { useAppDispatch } from "src/store/hooks";
import { formattedDate } from "src/helpers/DateHelpers";

import { Button, Input, Pagination, Tag } from 'antd';
import { InboxOutlined, SyncOutlined } from '@ant-design/icons';
import Card from "src/componets/Card";
import { durationLabel, shortFromNow } from "./_serverHelpers";

interface SessionRow {
  player: string;
  xuid: string | null;
  joinedAt: string;
  leftAt: string | null;
  durationSeconds: number;
}

const PAGE_SIZE = 25;

// Join/leave history, newest first. Pagination is offset-based (crafty
// keeps the data and reports the total); the player filter re-queries
// from page one.
const _SessionsHistory = ({ server, onSelectPlayer }: {
  server: ServerInterface,
  onSelectPlayer?: (gamertag: string) => void
}) => {
  const dispatch = useAppDispatch();

  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [player, setPlayer] = useState('');

  const serverId = server.id;

  const load = useCallback(async (targetPage: number, playerFilter: string) => {
    setLoading(true);
    const resultAction = await dispatch(fetchServerSessions({
      id: serverId,
      params: {
        limit:  PAGE_SIZE,
        offset: (targetPage - 1) * PAGE_SIZE,
        player: playerFilter.trim() || undefined
      }
    }));
    setLoading(false);

    if (fetchServerSessions.fulfilled.match(resultAction)) {
      setSessions(resultAction.payload?.sessions || []);
      setTotal(resultAction.payload?.total || 0);
    }
  }, [serverId, dispatch]);

  useEffect(() => {
    load(1, '').catch(console.error);
  }, [load]);

  function search(value: string) {
    setPlayer(value);
    setPage(1);
    load(1, value).catch(console.error);
  }

  function paginate(nextPage: number) {
    setPage(nextPage);
    load(nextPage, player).catch(console.error);
  }

  return (
    <Card className='v-srv-sessions'>
      <div className='o-head'>
        <h4 className='v-content-title'>Sessions</h4>
        <span className='o-count'>{total}</span>

        <Input.Search
          className='o-filter'
          placeholder='Filter by gamertag'
          allowClear
          maxLength={32}
          onSearch={search}
        />

        <Button
          className='o-icon-btn'
          icon={<SyncOutlined />}
          title='Refresh'
          aria-label='Refresh sessions'
          loading={loading}
          onClick={() => load(page, player)}
        />
      </div>

      <div className='o-list'>
        {sessions.map((session, index) => (
          <div key={index} className='v-srv-member'>
            <div className='o-who'>
              <span className='o-avatar o-empty' />
              <div className='o-names'>
                <div
                  className={`o-tag ${onSelectPlayer ? 'o-clickable' : ''}`}
                  title={onSelectPlayer ? 'View player profile' : undefined}
                  onClick={() => onSelectPlayer?.(session.player)}
                >
                  {session.player}
                  {!session.leftAt && <span className='o-online' title='Still online' />}
                </div>
                <div className='o-sub'>
                  <span title={formattedDate(session.joinedAt).dateTime}>
                    joined {shortFromNow(session.joinedAt)}
                  </span>
                  {session.leftAt &&
                    <span title={formattedDate(session.leftAt).dateTime}>
                      left {shortFromNow(session.leftAt)}
                    </span>}
                </div>
              </div>
            </div>

            {!session.leftAt && <Tag color='green'>online</Tag>}

            <span className='o-duration'>{durationLabel(session.durationSeconds)}</span>
          </div>
        ))}
      </div>

      {!loading && !sessions.length &&
        <div className='v-srv-empty'>
          <InboxOutlined className='o-icon' />
          <div className='o-title'>No sessions recorded</div>
          <div className='o-hint'>
            {player.trim()
              ? 'Nothing matches this gamertag — try clearing the filter.'
              : 'Join/leave history shows up once players connect.'}
          </div>
        </div>
      }

      {total > PAGE_SIZE &&
        <Pagination
          className='o-pagination'
          size='small'
          current={page}
          pageSize={PAGE_SIZE}
          total={total}
          showSizeChanger={false}
          onChange={paginate}
        />
      }
    </Card>
  );
}

export default _SessionsHistory;
